import type { Task, TaskStatus } from "../../api/types";
import { t } from "../../i18n/t";
import { localDayKey } from "../../utils/dayKey";

/** @see localDayKey — one implementation, shared with tasks and orders. */
export const taskDayKey = localDayKey;

export function startOfTaskMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfTaskMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export function sameTaskDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function localIsoFromDateTime(date: string, timeHHMM: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = timeHHMM.split(":").map(Number);
  return new Date(year, month - 1, day, hour, minute, 0, 0).toISOString();
}

export function dateInputFromIso(iso: string): string {
  return taskDayKey(new Date(iso));
}

export function timeInputFromIso(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function minutesToTime(minutes: number): string {
  const bounded = Math.max(0, Math.min(1439, minutes));
  const h = Math.floor(bounded / 60);
  const m = bounded % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function timeToMinutes(timeHHMM: string): number {
  const [hour, minute] = timeHHMM.split(":").map(Number);
  return hour * 60 + minute;
}

export function taskStatusClass(status: TaskStatus): string {
  if (status === "COMPLETED") return "badge success";
  if (status === "CANCELLED") return "badge danger";
  return "badge muted";
}

export function taskStatusLabel(task: Task): string {
  if (task.status !== "PENDING") return task.status.toLowerCase();
  return new Date(task.scheduledFor).getTime() < Date.now() ? "overdue" : "pending";
}

/**
 * Display ordering bucket for a task list: overdue first, then today's pending,
 * then completed, then cancelled. Sort by this rank, then by scheduledFor.
 */
export function taskOrderRank(task: Task, now: number = Date.now()): number {
  if (task.status === "PENDING") {
    return new Date(task.scheduledFor).getTime() < now ? 0 : 1;
  }
  if (task.status === "COMPLETED") return 2;
  return 3; // CANCELLED
}

export function compareTasksForDisplay(a: Task, b: Task, now: number = Date.now()): number {
  const rankDiff = taskOrderRank(a, now) - taskOrderRank(b, now);
  if (rankDiff !== 0) return rankDiff;
  return new Date(a.scheduledFor).getTime() - new Date(b.scheduledFor).getTime();
}

export function taskAudienceLabel(task: Task): string {
  return task.assignee ? task.assignee.fullName : t("taskUi.wholeShop");
}

export function appendTaskFormValue(form: FormData, key: string, value: string | null | undefined) {
  if (value == null) return;
  const trimmed = value.trim();
  if (trimmed) form.append(key, trimmed);
}
